import { Plus, X } from 'lucide-react';
import { generateRubricPointId, type AiDynamicSectionRubricPoint } from './aiDynamicSection';

export interface AiDynamicSectionRubricEditorProps {
  rubric: AiDynamicSectionRubricPoint[];
  onChange: (rubric: AiDynamicSectionRubricPoint[]) => void;
}

/** Tier a freshly author-added point starts at (1 = functional, the same middle-tier convention
 *  `questionCardHelpers.ts::DEFAULT_RUBRIC_POINT_TIER` uses for the sibling per-question rubric).
 *  Tier itself is not exposed for editing here — see the "leave as-is" call in
 *  AiDynamicSectionSettings.tsx. */
const NEW_RUBRIC_POINT_TIER = 1;

const RUBRIC_TIER_LABELS: Record<number, string> = {
  0: 'Tier 0 (core)',
  1: 'Tier 1 (functional)',
  2: 'Tier 2 (value)',
};

const rubricTierLabel = (tier: number): string => RUBRIC_TIER_LABELS[tier] ?? `Tier ${tier}`;

const RUBRIC_EDIT_HELPER_TEXT =
  'What a great answer should cover, generated by AI from the Goal, Topics, probing instructions, '
  + 'and linked questions above. Correct a point directly below, or update those inputs and '
  + 'regenerate — editing a point here never marks this checklist out of date.';

const POINT_TEXT_PLACEHOLDER = 'What should a great answer cover?';
const POINT_GUIDANCE_PLACEHOLDER = "Acceptance bar (optional) — what must actually be said for this to count as covered?";
const ADD_POINT_BUTTON_TEXT = 'Add point';

/**
 * Direct editing of an already-generated rubric, in the edit panel only (the read-only summary
 * keeps using `AiDynamicSectionRubricChecklist`, which has no edit affordances). The rubric stays
 * AI-generated by default — this is for CORRECTING it, not authoring it from scratch: a point's
 * text and acceptance-bar guidance are editable, and a point can be added or removed. Tier is
 * shown but not editable here (see the caller's docstring for why).
 *
 * A point's `id` is never touched once assigned — `AiDynamicSectionRunner`'s
 * `targets_rubric_points` and recorded coverage key on it, so renumbering an existing id on
 * add/remove would silently re-point history at the wrong point. `removePoint` filters by id, not
 * index, and `addPoint` only ever appends a freshly generated id.
 */
export function AiDynamicSectionRubricEditor({ rubric, onChange }: AiDynamicSectionRubricEditorProps) {
  const updatePoint = (id: string, patch: Partial<AiDynamicSectionRubricPoint>) =>
    onChange(rubric.map((point) => (point.id === id ? { ...point, ...patch } : point)));

  const removePoint = (id: string) => onChange(rubric.filter((point) => point.id !== id));

  const addPoint = () => {
    const id = generateRubricPointId(rubric.map((point) => point.id));
    onChange([...rubric, { id, text: '', tier: NEW_RUBRIC_POINT_TIER }]);
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">{RUBRIC_EDIT_HELPER_TEXT}</p>
      <div className="space-y-2">
        {rubric.map((point, index) => (
          <div key={point.id} className="border border-gray-100 rounded-lg p-2 space-y-1">
            <div className="flex items-start gap-2">
              <textarea
                aria-label={`Text for rubric point ${index + 1}`}
                value={point.text}
                onChange={(e) => updatePoint(point.id, { text: e.target.value })}
                className="flex-1 text-sm text-gray-800 border border-gray-200 rounded-md px-2 py-1"
                rows={1}
                placeholder={POINT_TEXT_PLACEHOLDER}
              />
              <span className="mt-1 text-xs text-gray-500 whitespace-nowrap">{rubricTierLabel(point.tier)}</span>
              <button
                type="button"
                onClick={() => removePoint(point.id)}
                aria-label={`Remove rubric point ${index + 1}`}
                className="p-1 text-gray-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              aria-label={`Acceptance bar for rubric point ${index + 1}`}
              value={point.guidance ?? ''}
              onChange={(e) => updatePoint(point.id, { guidance: e.target.value })}
              className="w-full text-xs text-gray-600 border border-gray-200 rounded-md px-2 py-1"
              rows={1}
              placeholder={POINT_GUIDANCE_PLACEHOLDER}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addPoint}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
      >
        <Plus className="w-3.5 h-3.5" /> {ADD_POINT_BUTTON_TEXT}
      </button>
    </div>
  );
}
